import { Component, OnInit, Input, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLinkActive, RouterLink, RouterOutlet } from "@angular/router";

import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities, RECENT_OPERATIONS_LIMIT } from "src/app/dvs-utilities";
import { OperationLabelPipe, OperationsLimitNotePipe } from "src/app/pipes";
import { ActionButtonComponent } from "src/app/shared/action-button/action-button.component";
import { AdjustmentGridComponent } from "src/app/shared/adjustment-grid/adjustment-grid.component";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { ShaftOrderComponent } from "src/app/shared/shaft-order/shaft-order.component";
import { APIResults } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-vibe-coefficients",
	templateUrl: "./vibe-coefficients.component.html",
	styleUrls: ["./vibe-coefficients.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormsModule, ActionButtonComponent, RouterLinkActive, RouterLink, RouterOutlet, DvsChartComponent, ShaftOrderComponent, AdjustmentGridComponent, OperationLabelPipe, OperationsLimitNotePipe]
})
export class VibeCoefficientsComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);
	readonly RECENT_OPERATIONS_LIMIT = RECENT_OPERATIONS_LIMIT;
	private route = inject(ActivatedRoute);
	private router = inject(Router);

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;

	public operations: APIResults.Operation[];
	public vshafts?: VShaftWithPlot[];
	public defaultVshaft = undefined;
	public selectedVshaft = undefined;
	public shaftOrder = 1;
	public adjustments: number[][];
	public opBefore: APIResults.Operation;
	public opAfter: APIResults.Operation;

	public timelineOptions: Highcharts.Options;

	public coefficientText?: string;
	public errormessage?: string;

	// TODO: Bug: if a regular RTB plot (vibe tab) is still in roundtrip, when it comes back it can overwrite this one (coeff tab)
	async ngOnInit() {
		this.vshafts = this.rotor.vshaft;
		const findDefault = this.vshafts.find(item => item.default);
		if (findDefault) {
			this.defaultVshaft = findDefault.name;
			this.selectedVshaft = findDefault;
		} else {
			this.defaultVshaft = this.vshafts[0].name;
			this.selectedVshaft = this.vshafts[0];
		}

		// Pick a chart tab if none selected or route is invalid
		const routeVshaftId = this.route?.firstChild?.snapshot?.params["vshaftid"];
		if (!routeVshaftId || !this.vshafts.find(item => item.id === routeVshaftId)) {
			let defaultVshaft = this.vshafts[0]?.id;
			const findDefault = this.vshafts.find(item => item.default);
			if (findDefault) defaultVshaft = findDefault.id;
			this.router.navigate([defaultVshaft], { relativeTo: this.route, replaceUrl: true });
		}

		// Initialize empty adjustments - note must make sure rows are different arrays
		this.adjustments = Array(this.rotor.balancedata.numadjustmenttypes).fill([]).map(() => Array(this.rotor.balancedata.numblades).fill(0));
		await this.refreshOperations();
	}

	rotorChange() {
		this.ngOnInit();
	}

	async refreshOperations() {
		this.operations = undefined;
		const operations = await this.api.GetRTBOperations(this.asset.asset_id, this.rotor.rotorid, false, RECENT_OPERATIONS_LIMIT);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.opBefore = selectedOp;
		}
		this.opAfter = this.opBefore;
		await this.updateBothPlots();
	}

	async updateBothPlots() {
		await Promise.all([
			this.updatePlot(),
			this.updateTimeline(),
		]);
	}

	async updatePlot() {
		this.errormessage = undefined;
		if (this.opBefore) {
			// Clear the plots
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				const [beforeRegimes, afterRegimes] = await Promise.all([
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id),
					this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.opAfter.operation_id),
				]);
				const badops = [];
				if (beforeRegimes.vibe.length === 0) {
					badops.push("Before");
				}
				if (afterRegimes.vibe.length === 0) {
					badops.push("After");
				}
				if (badops.length > 0) {
					this.errormessage = "No data for " + badops.join(", ") + " operation";
					return;
				}
				const allOn = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
				const [beforeResults, afterResults] = await Promise.all([
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, [this.opBefore.operation_id], [], this.shaftOrder, allOn, false, true),
					this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, [this.opAfter.operation_id], [], this.shaftOrder, allOn, false, true),
				]);
				if (this.vshafts) {
					for (const vshaft of this.vshafts) {
						const beforeComp = beforeResults.data[vshaft.shftid];
						const afterComp = afterResults.data[vshaft.shftid];
						if (beforeComp && afterComp) {
							const series: Highcharts.SeriesScatterOptions[] = [];
							const allRegimes = Object.keys({ ...beforeComp.regimes, ...afterComp.regimes });
							for (const regimeKey of allRegimes) {
								let regime = this.regimeConfigs[Number(regimeKey)];
								if (!regime) regime = { name: "Unknown regime " + regimeKey, default: false };
								const points: any[] = [];
								if (beforeComp.regimes[regimeKey]) {
									points.push({
										x: DvsUtilities.AddDegreeOffset(beforeComp.regimes[regimeKey].average[0], vshaft.rotate),
										y: beforeComp.regimes[regimeKey].average[1],
										name: "Before Average",
										marker: { symbol: "square" },
									});
								}
								if (afterComp.regimes[regimeKey]) {
									points.push({
										x: DvsUtilities.AddDegreeOffset(afterComp.regimes[regimeKey].average[0], vshaft.rotate),
										y: afterComp.regimes[regimeKey].average[1],
										name: "After Average",
										marker: { symbol: "diamond" },
									});
								}
								series.push({
									name: regime.name,
									type: "scatter",
									data: points,
									marker: { symbol: "circle" },
								});
							}
							vshaft.plotOptions = this.charting.RTBScatterDefaults(series);
						} else {
							vshaft.plotOptions = undefined;
						}
					}
				}
			} catch (err) {
				console.log(err);
				this.errormessage = DvsUtilities.GetRemoteErrorMessage(err, "Couldn't load RTB data");
			}
		}
	}

	async changeTab(event) {
		this.selectedVshaft = this.vshafts.find(item => item.name === event.nextId);
		await this.updateTimeline();
	}
	async updateTimeline() {
		this.timelineOptions = undefined;
		const [adjustments, history] = await Promise.all([
			this.api.GetAdjustmentHistory(this.asset.asset_id, this.rotor.rotorid),
			this.api.GetRTBHistory(this.asset.asset_id, this.rotor.rotorid, this.selectedVshaft.shftid, [], this.shaftOrder),
		]);
		const timelineSeries: Highcharts.SeriesLineOptions[] = [];
		for (const regimeData of history.regimes) {
			let regimeLookup = this.regimeConfigs[regimeData.regime];
			if (!regimeLookup) regimeLookup = { name: "Unknown regime " + regimeData.regime, default: false };
			const polarPoints: any[] = [];
			const timelinePoints: any[] = [];
			for (const op of regimeData.operations) {
				polarPoints.push({ x: op.phase, y: op.mag, time: op.ts });
				timelinePoints.push({ x: op.ts * 1000, y: op.mag });
			}
			timelineSeries.push({
				name: regimeLookup.name,
				type: "line",
				data: timelinePoints,
			});
		}
		this.timelineOptions = this.charting.RTBHistoryTimelineDefaults(timelineSeries, "Magnitude (IPS)", this.fleet ? this.fleet.time_offset_hours : 0, adjustments, undefined, this);
	}

	async CalculateCoefficients() {
		this.coefficientText = undefined;
		const result = await this.api.GetRTBCoefficients(this.asset.asset_id, this.rotor.rotorid, this.opBefore.operation_id, this.opAfter.operation_id, this.adjustments);
		this.coefficientText = result.data.join("\n");
		setTimeout(() => {
			const resultElement = document.getElementById("coefficient-results");
			resultElement?.scrollIntoView({ behavior: "smooth" });
		}, 100);
	}

	private activeRouteComponent: any;
	public updateRouterOutlet(routecomponent?: any) {
		if (routecomponent) {
			this.activeRouteComponent = routecomponent;
		}
		if (this.activeRouteComponent) {
			const vshaftid = this.route?.firstChild?.snapshot?.params["vshaftid"];
			const vshaft = this.vshafts.find(item => item.id === vshaftid);
			this.activeRouteComponent.vshaft = vshaft;
			this.activeRouteComponent.avgLabel = "Before Averages";
			this.activeRouteComponent.predLabel = "After Averages";
			this.activeRouteComponent.disableDownload = true;
			this.activeRouteComponent.disableInteractive = true;
		}
	}
}

interface VShaftWithPlot extends APIResults.VShaft {
	plotOptions?: Highcharts.Options;
}
